import { useEffect } from 'react';
import { isSessionExpired, shouldRenewSession, type ItemResponse, type SessionResponse } from '@proerp/shared';
import { portalRequest } from './apiClient';
import { currentEndReason, endSession, getStoredToken, storeToken } from './auth';

/**
 * Hält die Partner-Sitzung am Leben, solange jemand arbeitet — und beendet sie
 * nach 12 h Ruhe — spätestens beim nächtlichen Schnitt um 3 Uhr (ADR 0106/0107). Gegenstück zu `apps/web/src/lib/useSessionWatch.ts`;
 * die Zeitrechnung teilen sich beide über `@proerp/shared`, damit Karenz und
 * Verlängerungsschwelle nicht auseinanderlaufen.
 *
 * Eigene Datei statt Wiederverwendung, weil die Sitzung selbst getrennt ist:
 * anderer Speicherschlüssel, anderer Endpunkt (`/v1/portal/session/refresh` —
 * ein Partner-Token ist an internen Pfaden bewusst wertlos, ADR 0085).
 */

const CHECK_INTERVAL_MS = 60_000;
/** Aktivität = bewusste Eingabe. Kein `mousemove`: Ein Mauszucken ist kein Arbeiten. */
const ACTIVITY_EVENTS = ['pointerdown', 'keydown'] as const;

/**
 * Sitzung verlängern. Fehler werden verschluckt: Bei 401 wirft der `PortalGate`
 * den Partner ohnehin auf die Anmeldung, bei einem Netzfehler wäre ein Rauswurf
 * falsch — es bleiben Stunden, der nächste Klick versucht es erneut.
 */
async function renewSession(): Promise<void> {
  try {
    const response = await portalRequest<ItemResponse<SessionResponse>>('/session/refresh', { method: 'POST' });
    storeToken(response.data.accessToken);
  } catch {
    // siehe oben
  }
}

export function useSessionWatch(): void {
  useEffect(() => {
    let renewing = false;

    function checkExpiry(): void {
      if (isSessionExpired(getStoredToken())) endSession(currentEndReason());
    }

    function handleActivity(): void {
      const token = getStoredToken();
      // Erst der Ablauf: Wer nach 12 h Ruhe wieder klickt, wird abgemeldet und
      // verlängert nicht mit demselben Klick.
      if (isSessionExpired(token)) {
        endSession(currentEndReason());
        return;
      }
      if (renewing || !shouldRenewSession(token)) return;
      renewing = true;
      void renewSession().finally(() => {
        renewing = false;
      });
    }

    function handleVisibility(): void {
      if (document.visibilityState === 'visible') checkExpiry();
    }

    const timer = window.setInterval(checkExpiry, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', handleVisibility);
    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, handleActivity, { capture: true, passive: true });
    }
    checkExpiry();

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibility);
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, handleActivity, { capture: true });
      }
    };
  }, []);
}
